import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Paper,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
  Tooltip,
  Fab,
  Divider,
  LinearProgress,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Timeline as TimelineIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Flag as FlagIcon,
  Send as SendIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import DetalleDialog from '../components/DetalleDialog';

// Estados con configuración de colores
const estadosConfig = {
  1: { nombre: 'Pendiente', color: '#FB8C00', bgColor: '#FFF3E0', icon: WarningIcon },
  2: { nombre: 'Asignado', color: '#1E88E5', bgColor: '#E3F2FD', icon: PersonIcon },
  3: { nombre: 'En Camino', color: '#7B1FA2', bgColor: '#F3E5F5', icon: LocationIcon },
  4: { nombre: 'En Proceso', color: '#43A047', bgColor: '#E8F5E8', icon: PlayArrowIcon },
  5: { nombre: 'Terminado', color: '#2E7D32', bgColor: '#E8F5E8', icon: CheckCircleIcon },
  6: { nombre: 'En Espera', color: '#FFA000', bgColor: '#FFF8E1', icon: PauseIcon },
  7: { nombre: 'Cancelado', color: '#C62828', bgColor: '#FFEBEE', icon: CancelIcon }
};

// Componente de Progreso de Orden
const ProgresoOrden = ({ orden }) => {
  const indicePorcentaje = {
    1: 20, 2: 40, 3: 60, 4: 80, 5: 100, 6: 10, 7: 0
  };
  
  const progreso = indicePorcentaje[parseInt(orden?.id_estado_servicio)] || 0;
  const color = orden?.id_estado_servicio === '7' ? 'error' : progreso === 100 ? 'success' : 'primary';

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Progreso
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {progreso}%
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={progreso} 
        color={color}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
};


// Componente de Tarjeta de Orden para vista móvil
const OrdenCard = ({ orden, onView, onEdit, onDelete }) => {
  const estadoConfig = estadosConfig[parseInt(orden.id_estado_servicio)] || {};
  const IconComponent = estadoConfig.icon || AssignmentIcon;

  return (
    <Card sx={{ 
      mb: 2,
      borderRadius: 3,
      border: '1px solid rgba(26, 35, 126, 0.1)',
      borderLeft: `4px solid ${estadoConfig.color || '#1976d2'}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(26, 35, 126, 0.15)'
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
            OS #{orden.id_orden_servicio}
          </Typography>
          <Chip 
            icon={<IconComponent sx={{ fontSize: 16 }} />}
            label={estadoConfig.nombre || orden.nombre_estado_servicio}
            size="small"
            sx={{ 
              bgcolor: estadoConfig.bgColor,
              color: estadoConfig.color,
              fontWeight: 'bold'
            }}
          />
        </Box>

        {/* Cliente */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Cliente
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {orden.nombre_cliente} {orden.apellido_cliente}
          </Typography>
        </Box>

        {/* Tipo de servicio */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Tipo de Servicio
          </Typography>
          <Typography variant="body2">
            {orden.tipo_servicio || 'No especificado'}
          </Typography>
        </Box>

        {/* Progreso */}
        <ProgresoOrden orden={orden} />

        {/* Personal asignado */}
        {orden.trabajadores && orden.trabajadores.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Personal Asignado
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {orden.trabajadores.slice(0, 2).map((trabajador, index) => (
                <Chip
                  key={index}
                  avatar={<Avatar sx={{ bgcolor: '#1A237E' }}>{trabajador.nombre_trabajador?.[0]}</Avatar>}
                  label={`${trabajador.nombre_trabajador} ${trabajador.apellido_trabajador}`}
                  size="small"
                  variant="outlined"
                />
              ))}
              {orden.trabajadores.length > 2 && (
                <Chip
                  label={`+${orden.trabajadores.length - 2} más`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          </Box>
        )}

        {/* Fecha */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'text.secondary' }}>
          <ScheduleIcon sx={{ mr: 1, fontSize: 16 }} />
          <Typography variant="caption">
            {new Date(orden.fecha_orden).toLocaleString()}
          </Typography>
        </Box>

        {/* Acciones */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Tooltip title="Ver detalles">
            <IconButton 
              size="small" 
              onClick={() => onView(orden)}
              sx={{ bgcolor: 'info.light', color: 'white', '&:hover': { bgcolor: 'info.main' } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar orden">
            <IconButton 
              size="small" 
              onClick={() => onEdit(orden)}
              sx={{ bgcolor: 'primary.light', color: 'white', '&:hover': { bgcolor: 'primary.main' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar orden">
            <IconButton 
              size="small" 
              onClick={() => onDelete(orden)}
              sx={{ bgcolor: 'error.light', color: 'white', '&:hover': { bgcolor: 'error.main' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

function OrdenesServicioAdmin() {
  const [ordenes, setOrdenes] = useState([]);
  const [filteredOrdenes, setFilteredOrdenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrden, setSelectedOrden] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState('id_orden_servicio');
  const [order, setOrder] = useState('desc');
  const [error, setError] = useState(null);
  const [sendLinkDialog, setSendLinkDialog] = useState({ open: false, orden: null });
  const [sendLinkEmail, setSendLinkEmail] = useState('');
  const [sendLinkLoading, setSendLinkLoading] = useState(false);
  const [sendLinkResult, setSendLinkResult] = useState(null);

  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchOrdenes();
  }, []);

  useEffect(() => {
    const filterAndSortOrdenes = () => {
      let filtered = [...ordenes];
      
      if (searchTerm) {
        filtered = filtered.filter(orden =>
          Object.values(orden)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      }

      filtered.sort((a, b) => {
        const isAsc = order === 'asc';
        if (a[orderBy] < b[orderBy]) return isAsc ? -1 : 1;
        if (a[orderBy] > b[orderBy]) return isAsc ? 1 : -1;
        return 0;
      });

      setFilteredOrdenes(filtered);
      setPage(0);
    };

    filterAndSortOrdenes();
  }, [ordenes, searchTerm, order, orderBy]);

  const fetchOrdenes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/ordenes`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setOrdenes(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al obtener las órdenes de servicio:', error);
      setError('Error al cargar las órdenes. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendLink = async () => {
    if (!sendLinkEmail) return;
    setSendLinkLoading(true);
    setSendLinkResult(null);
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/v1/tracking-links/generar`, {
        id_orden_servicio: sendLinkDialog.orden.id_orden_servicio,
        email_destino: sendLinkEmail,
        enviar_email: true,
      }, { headers: { Authorization: `Bearer ${user.token}` } });
      setSendLinkResult({ type: 'success', link: res.data.link, msg: `Link enviado a ${sendLinkEmail}` });
    } catch (err) {
      setSendLinkResult({ type: 'error', msg: err.response?.data?.error || 'Error al enviar' });
    } finally {
      setSendLinkLoading(false);
    }
  };

  const openSendLinkDialog = (orden) => {
    setSendLinkDialog({ open: true, orden });
    setSendLinkEmail('');
    setSendLinkResult(null);
  };

  const handleDelete = async (orden) => {
    if (window.confirm(`¿Estás seguro de desactivar la orden ${orden.id_orden_servicio}?`)) {
      try {
        await axios.delete(`${config.API_BASE_URL}/api/ordenes/${orden.id_orden_servicio}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchOrdenes();
      } catch (error) {
        console.error('Error al desactivar la orden de servicio:', error);
        setError('Error al eliminar la orden.');
      }
    }
  };

  const handleView = (orden) => {
    setSelectedOrden(orden);
    setOpenDialog(true);
  };

  const handleEdit = (orden) => {
    navigate(`/home/ordenes/editar/${orden.id_orden_servicio}`);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1"
            sx={{ 
              fontWeight: 'bold',
              color: '#1A237E',
              mb: 0.5
            }}
          >
            🎯 Gestión de Órdenes de Servicio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra y supervisa todas las órdenes de servicio
          </Typography>
        </Box>

        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/home/ordenes/crear')}
            sx={{
              background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
              boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Crear Orden de Servicio
          </Button>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Barra de búsqueda */}
      <Box sx={{ mb: 3 }}>
        <TextField
          variant="outlined"
          fullWidth={isMobile}
          size="small"
          placeholder="Buscar por número, cliente, estado..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#4FC3F7' }} />
              </InputAdornment>
            ),
          }}
          sx={{ 
            width: isMobile ? '100%' : 400,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: '#4FC3F7',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1A237E',
              },
            }
          }}
        />
      </Box>

      {/* Vista móvil - Cards */}
      {isMobile ? (
        <Box>
          {filteredOrdenes
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((orden) => (
              <OrdenCard 
                key={orden.id_orden_servicio} 
                orden={orden}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          
          {filteredOrdenes.length > rowsPerPage && (
            <Paper sx={{ mt: 2, borderRadius: 2 }}>
              <TablePagination
                rowsPerPageOptions={[20]}
                component="div"
                count={filteredOrdenes.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
                labelRowsPerPage="Filas por página:"
              />
            </Paper>
          )}
        </Box>
      ) : (
        /* Vista desktop - Tabla mejorada */
        <Paper sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(26, 35, 126, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ 
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)' 
              }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'id_orden_servicio'}
                      direction={orderBy === 'id_orden_servicio' ? order : 'asc'}
                      onClick={createSortHandler('id_orden_servicio')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      # OS
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Tipo
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Cliente
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'fecha_orden'}
                      direction={orderBy === 'fecha_orden' ? order : 'asc'}
                      onClick={createSortHandler('fecha_orden')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Fecha OS
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Estado
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Periodo
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrdenes
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((orden, index) => {
                    const estadoConfig = estadosConfig[parseInt(orden.id_estado_servicio)] || {};
                    const IconComponent = estadoConfig.icon || AssignmentIcon;
                    
                    return (
                      <TableRow 
                        key={orden.id_orden_servicio}
                        sx={{ 
                          '&:nth-of-type(even)': { 
                            backgroundColor: 'rgba(26, 35, 126, 0.02)' 
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(79, 195, 247, 0.1)',
                            transform: 'scale(1.01)',
                            transition: 'all 0.2s ease'
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
                            {orden.id_orden_servicio}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {orden.tipo_servicio || 'No especificado'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)',
                                color: 'white',
                                fontSize: '0.8rem',
                                fontWeight: 'bold'
                              }}
                            >
                              {orden.nombre_cliente?.[0]}{orden.apellido_cliente?.[0]}
                            </Avatar>
                            <Typography variant="body2">
                              {orden.nombre_cliente} {orden.apellido_cliente}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(orden.fecha_orden).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            icon={<IconComponent sx={{ fontSize: 14 }} />}
                            label={estadoConfig.nombre || orden.nombre_estado_servicio}
                            size="small"
                            sx={{ 
                              bgcolor: estadoConfig.bgColor,
                              color: estadoConfig.color,
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {orden.mes || ''}/{orden.anio || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Ver detalles">
                              <IconButton
                                size="small"
                                onClick={() => handleView(orden)}
                                sx={{ 
                                  bgcolor: 'info.light', 
                                  color: 'white',
                                  '&:hover': { bgcolor: 'info.main' }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar orden">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(orden)}
                                sx={{ 
                                  bgcolor: 'primary.light', 
                                  color: 'white',
                                  '&:hover': { bgcolor: 'primary.main' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar link al cliente">
                              <IconButton
                                size="small"
                                onClick={() => openSendLinkDialog(orden)}
                                sx={{
                                  bgcolor: '#25D366',
                                  color: 'white',
                                  '&:hover': { bgcolor: '#128C7E' }
                                }}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar orden">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(orden)}
                                sx={{
                                  bgcolor: 'error.light',
                                  color: 'white',
                                  '&:hover': { bgcolor: 'error.main' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[20]}
            component="div"
            count={filteredOrdenes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            labelRowsPerPage="Filas por página:"
            sx={{
              borderTop: '1px solid rgba(26, 35, 126, 0.1)',
              backgroundColor: 'rgba(26, 35, 126, 0.02)'
            }}
          />
        </Paper>
      )}

      {/* FAB para móvil */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={() => navigate('/home/ordenes/crear')}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
            }
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialog de detalles */}
      <DetalleDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        ordenId={selectedOrden?.id_orden_servicio}
      />

      {/* Dialog: Enviar Link de Seguimiento */}
      <Dialog
        open={sendLinkDialog.open}
        onClose={() => setSendLinkDialog({ open: false, orden: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #E2E8F0' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <LinkIcon sx={{ color: '#1A237E' }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">Enviar Link de Seguimiento</Typography>
              <Typography variant="body2" color="text.secondary">
                Orden #{sendLinkDialog.orden?.id_orden_servicio}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {sendLinkResult && (
            <Alert severity={sendLinkResult.type} sx={{ mb: 2 }}>
              {sendLinkResult.msg}
              {sendLinkResult.link && (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    fullWidth size="small" value={sendLinkResult.link}
                    InputProps={{ readOnly: true }}
                    onClick={(e) => { e.target.select(); navigator.clipboard.writeText(sendLinkResult.link); }}
                    helperText="Click para copiar"
                  />
                </Box>
              )}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se generará un link válido por 24 horas con el detalle y trazabilidad del servicio.
          </Typography>
          <TextField
            fullWidth
            label="Email del destinatario"
            type="email"
            value={sendLinkEmail}
            onChange={(e) => setSendLinkEmail(e.target.value)}
            placeholder="cliente@ejemplo.com"
            helperText="Se enviará un correo con el link de seguimiento"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E2E8F0' }}>
          <Button onClick={() => setSendLinkDialog({ open: false, orden: null })}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={sendLinkLoading ? null : <SendIcon />}
            onClick={handleSendLink}
            disabled={sendLinkLoading || !sendLinkEmail}
            sx={{ bgcolor: '#1A237E', '&:hover': { bgcolor: '#0D47A1' } }}
          >
            {sendLinkLoading ? 'Enviando...' : 'Enviar Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OrdenesServicioAdmin;